import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import combinedActions from '~redux/combinedActions';
import { TUpdateCustomerTransferStatusReduxParams } from '~types/feat/approval';
import { MetalTypeEnum, ModuleEnum, PermissionsEnum, TransactionStatusEnum } from '~utils/enums';
import UtilFunctions from '~utils/functions';
import Table from '~components/molecules/Table';
import ActionButton from '~components/atoms/ActionButton';
import * as Styles from './CustomerTransferApprovalTable.module.scss';
import ViewSerialNoModalModal from '~components/molecules/ViewSerialNoModal';

const CustomerTransferApprovalTable = () => {
    const dispatch = useAppDispatch();

    const { Misc: { dateTimeFormat, renderTransactionStatusChip } } = UtilFunctions;

    const customerTransferListing = useAppSelector(state => state.approval.pendingCustomerTransferListing.data).filter(item => item.status === TransactionStatusEnum.Pending);
    const isLoading = useAppSelector(state => state.approval.loadings.getPendingCustomerTransferListing);
    const access = useAppSelector(state => state.auth.userDetails.access);
    const filters = useAppSelector(state => state.approval.pendingCustomerTransferListing.filters);
    const allProducts = useAppSelector(state => state.gold.allProducts);

    const [serialNoList, setSerialNoList] = useState<string[]>([]);
    const [isSerialNoModalOpen, setIsSerialNoModalOpen] = useState(false);

    const hasEditAccess = access.find(item => item.permission === PermissionsEnum.Edit)?.modules.find(module => module === ModuleEnum.Approval);

    const setTempUpdateCustomerTransferState = (data: TUpdateCustomerTransferStatusReduxParams) =>
        dispatch(combinedActions.approvalSetTempUpdateCustomerTransferState(data));

    const setConfirmModalIsOpen = (open: boolean) => dispatch(combinedActions.approvalSetConfirmUpdateCustomerTransferStatusModalIsOpen(open));

    const handleApproveClick = (data: TUpdateCustomerTransferStatusReduxParams) => {
        setConfirmModalIsOpen(true);
        setTempUpdateCustomerTransferState(data);
    };

    const handleRejectClick = (data: TUpdateCustomerTransferStatusReduxParams) => {
        setConfirmModalIsOpen(true);
        setTempUpdateCustomerTransferState(data);
    };

    const tableHeader = [
        'TRANSACTION ID',
        'SENDER NAME',
        'SENDER CIF',
        'PRODUCT NAME',
        'METAL TYPE',
        'SERIAL NUMBER',
        'TOTAL WEIGHT (G)',
        'STATUS',
        'RECEIVER NAME',
        'RECEIVER CIF',
        'BRANCH CODE',
        'BRANCH NAME',
        'REGION NAME',
        'BRANCH TELLER NAME',
        'SALES PERSONNEL NAME',
        'REFERRAL NAME',
        'TRANSACTION DATE',
        'ACTIONS',
    ];

    const renderActionButton = (id: number, status: number) => {
        if (status === 1) {
            return (
                <div
                    key={id}
                    className={Styles['action-button-container']}
                >
                    {hasEditAccess
                        && (
                            <ActionButton
                                label='Approve'
                                onClick={() => handleApproveClick({ id, status: TransactionStatusEnum.Approved })}
                                className={`${Styles['action-button']} ${Styles.approve}`}
                            />
                        )
                    }

                    {hasEditAccess
                        && (
                            <ActionButton
                                label='Reject'
                                onClick={() => handleRejectClick({ id, status: TransactionStatusEnum.Rejected })}
                                className={`${Styles['action-button']} ${Styles.reject}`}
                            />
                        )
                    }
                </div>
            );
        }
        return '-';
    };

    const tableBody = customerTransferListing.map(item => {
        const {
            customerTransferId,
            senderName,
            senderCIFNo,
            productName,
            productId,
            metalType,
            serialNumbers,
            weight,
            status,
            receiverName,
            receiverCIFNo,
            branchCode,
            branchName,
            regionName,
            branchTellerName,
            salesPersonnelName,
            referralName,
            createdAt,
        } = item;

        const product = allProducts.find(product => String(product.productId) === String(productId));

        const isGold = product?.metalType === MetalTypeEnum.Gold;

        const handleViewSerialNo = () => {
            if (!serialNumbers) return;

            setIsSerialNoModalOpen(true);
            setSerialNoList(serialNumbers);
        };

        const renderSerialNo = () => {
            if (!serialNumbers) return '-';

            return (
                <ActionButton
                    testId='customerTransfer-view-serialNo-modal-button'
                    key={customerTransferId}
                    label='View'
                    onClick={() => handleViewSerialNo()}
                />
            );
        };

        return {
            className: isGold ? Styles['gold-row'] : '',
            cells: [
                customerTransferId,
                senderName,
                senderCIFNo,
                productName,
                MetalTypeEnum[metalType],
                renderSerialNo(),
                weight !== null && weight !== undefined
                    ? weight.toFixed(3)
                    : '-',
                renderTransactionStatusChip(status, TransactionStatusEnum[status]),
                receiverName,
                receiverCIFNo,
                branchCode,
                branchName,
                regionName,
                branchTellerName || '-',
                salesPersonnelName || '-',
                referralName || '-',
                dateTimeFormat(createdAt) || '-',
                renderActionButton(customerTransferId, status),
            ],
        };
    });

    return (
        <>
            <Table
                header={tableHeader}
                body={tableBody}
                isLoading={isLoading}
            />

            <ViewSerialNoModalModal
                isOpen={isSerialNoModalOpen}
                onClose={() => setIsSerialNoModalOpen(false)}
                serialNo={serialNoList}
            />
        </>
    );
};

export default CustomerTransferApprovalTable;
